import React, { FC, useCallback, useState, useMemo, Dispatch, SetStateAction } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { track } from 'utils/analytics'
import { Calendar, Event } from '../../types'
import CoreText from 'components/Core/CoreText'
import CoreButton from 'components/Core/CoreButton'
import CoreDivider from 'components/Core/CoreDivider'
import CalendarComponent, { CalendarMarks, setMark } from '../../Availabilities/Calendar'
import { Block } from '../../Availabilities/Time'
import ValidationCheck from './ValidationCheck'
import Slots from './Slots'

dayjs.extend(utc)
dayjs.extend(timezone)

interface UpdateAvailabilityProps {
  calendar: Calendar
  event: Event
  availabilities: Block[]
  setAvailabilities: Dispatch<SetStateAction<Block[]>>
  adjustedNow: Date
  sendingRescheduleRequest: boolean
  setSendingRescheduleRequest: (sendingRescheduleRerquest: boolean) => void
}

const UpdateAvailability: FC<UpdateAvailabilityProps> = ({
  calendar,
  event,
  availabilities,
  setAvailabilities,
  adjustedNow,
  sendingRescheduleRequest,
  setSendingRescheduleRequest,
}) => {
  const [selectedDate, setSelectedDate] = useState(() => {
    if (availabilities.length > 0) {
      return availabilities[0].start
    }
    return new Date()
  })

  const daysWithAvailability = useMemo(() => {
    return availabilities.reduce((days, block) => {
      // A block could span more than 1 day.
      const start = dayjs(block.start).tz(calendar.property.time_zone).startOf('day')
      const end = dayjs(block.end).tz(calendar.property.time_zone).add(1, 'day').startOf('day')
      const numDays = end.diff(start, 'days')
      for (let d = 0; d < numDays; d++) {
        const day = start.add(d, 'days')
        const iDay = day.toDate().getTime()
        if (!days.some((existingDay) => existingDay.toDate().getTime() == iDay)) {
          days = [...days, day]
        }
      }
      return days
    }, [] as Dayjs[])
  }, [availabilities, calendar.property.time_zone])

  const marks = useMemo(() => {
    return daysWithAvailability.reduce((marks, day) => {
      return setMark(marks, day, 'default')
    }, {} as CalendarMarks)
  }, [daysWithAvailability])

  const handleConfirm = useCallback(() => {
    track('click', { family: 'homeowner-site-visit', screen: 'reschedule', section: 'availability', code: 'confirm' })
    setSendingRescheduleRequest(true)
  }, [setSendingRescheduleRequest])

  const handleTrackAttempted = useCallback((code: string, flexfield_1: any) => {
    track('click', { family: 'homeowner-site-visit', screen: 'reschedule', section: 'availability', code, flexfield_1 })
  }, [])

  const handleCalculateValidDate = useCallback(
    (date: Dayjs) => {
      const daydate = date.startOf('day')
      // Show nothing before now
      const sodNow = dayjs(adjustedNow).tz(calendar.property.time_zone).startOf('day')
      return daydate.isAfter(sodNow)
    },
    [calendar.property.time_zone, adjustedNow]
  )

  const handleSlotsChanged = useCallback(
    (start: Date, end: Date, selected: boolean) => {
      setAvailabilities((prev) => {
        let newBlock: Block | null = { start, end }

        if (selected) {
          // Add a new block in at the correct spot (ordered by start)
          prev = prev.reduce((all, block) => {
            if (newBlock && block.start.getTime() > newBlock.start.getTime()) {
              all = [...all, newBlock]
              newBlock = null
            }
            return [...all, block]
          }, [] as Block[])
          // If it wasn't inserted yet, add it to the end.
          if (newBlock) prev.push(newBlock)

          // Now make sure we don't have any overlap.
          prev = prev.reduce((all, block) => {
            const last: null | Block = all.slice(-1)[0] || null
            const rest = all.slice(0, -1)
            if (!last) return [block]

            // If our block starts before the last block ends,
            // they must be merged
            if (last.end.getTime() >= block.start.getTime()) {
              return [
                ...rest,
                {
                  // Because we're ordered, we never will have the current block's
                  // start time less than the last block's start time.
                  start: last.start,
                  end: new Date(Math.max(last.end.getTime(), block.end.getTime())),
                },
              ]
            }
            return [...all, block]
          }, [] as Block[])
        } else {
          // Subtract our new block from every existing block.
          prev = prev.reduce((all, block) => {
            if (!newBlock) return [...all, block]

            if (newBlock.start.getTime() <= block.start.getTime() && newBlock.end.getTime() >= block.end.getTime()) {
              // If newBlock covers the entirety of block, then block can be
              // dropped
              return all
            } else if (
              newBlock.start.getTime() > block.start.getTime() &&
              newBlock.end.getTime() < block.end.getTime()
            ) {
              // If newBlock is 'inside' block, then split block in two.
              return [
                ...all,
                {
                  start: block.start,
                  end: newBlock.start,
                },
                {
                  start: newBlock.end,
                  end: block.end,
                },
              ]
            } else if (newBlock.start.getTime() <= block.start.getTime()) {
              // Truncate start of current block
              return [
                ...all,
                {
                  start: new Date(Math.max(newBlock.end.getTime(), block.start.getTime())),
                  end: block.end,
                },
              ]
            } else if (newBlock.end.getTime() >= block.end.getTime()) {
              // Truncate end of current block
              return [
                ...all,
                {
                  start: block.start,
                  end: new Date(Math.min(newBlock.start.getTime(), block.end.getTime())),
                },
              ]
            }

            return [...all, block]
          }, [] as Block[])
        }

        return prev
      })
    },
    [setAvailabilities]
  )

  const validThreeDays = useMemo(() => daysWithAvailability.length >= 3, [daysWithAvailability])

  const validOneWeekday = useMemo(() => {
    return daysWithAvailability.some((day) => [1, 2, 3, 4, 5].includes(day.day()))
  }, [daysWithAvailability])

  const validMinHours = useMemo(() => {
    const availableHours = availabilities.reduce((a, e) => {
      const hoursWithinBlock = dayjs(e.end).diff(dayjs(e.start), 'hours')
      return a + hoursWithinBlock
    }, 0)
    return availableHours >= calendar.number_of_vendors_to_schedule * 3
  }, [availabilities, calendar.number_of_vendors_to_schedule])

  const valid = useMemo(() => {
    return validThreeDays && validOneWeekday && validMinHours
  }, [validThreeDays, validOneWeekday, validMinHours])

  return (
    <div>
      <div className="tw:flex tw:flex-col tw:gap-1 tw:m-4 tw:text-center">
        <CoreText.Headline size="xs" weight="heavy" color="primaryBlack">
          {'Confirm or update your availability for vendors to reschedule the site visit'}
        </CoreText.Headline>
        <CoreText.Paragraph size="m" weight="light" color="tertiaryBlack">
          {'Please confirm or update your availability so vendors can reliably schedule site visits.'}
        </CoreText.Paragraph>
      </div>
      <div className="tw:flex tw:flex-col tw:gap-6 tw:bg-white tw:border tw:border-neutrals-100 tw:py-6 tw:rounded-2xl tw:shadow-xl">
        <CalendarComponent
          className="tw:px-4"
          selected={selectedDate}
          onSelectionChange={setSelectedDate}
          marks={marks}
          timezone={calendar.property.time_zone}
          allowViewChange={false}
          onTrackAttempt={handleTrackAttempted}
          onCalculateValidDate={handleCalculateValidDate}
        />
        <CoreDivider.Full />
        <Slots
          event={event}
          className="tw:px-4"
          selectedDate={selectedDate}
          timezone={calendar.property.time_zone}
          adjustedNow={adjustedNow}
          availabilities={availabilities}
          events={calendar.events.filter((e) => ['scheduled', 'occuring'].includes(e.status))}
          onChange={handleSlotsChanged}
        />

        <div className="tw:flex tw:flex-col tw:gap-3 tw:px-4">
          <ValidationCheck valid={validThreeDays}>
            <CoreText.Paragraph size="s" weight="light" color="secondaryBlack">
              {'Available across at least 3 days'}
            </CoreText.Paragraph>
          </ValidationCheck>
          <ValidationCheck valid={validOneWeekday}>
            <CoreText.Paragraph size="s" weight="light" color="secondaryBlack">
              {'Availability must include at least one weekday'}
            </CoreText.Paragraph>
          </ValidationCheck>
          {calendar.number_of_vendors_to_schedule > 0 && (
            <ValidationCheck valid={validMinHours}>
              <CoreText.Paragraph size="s" weight="light" color="secondaryBlack">
                {`At least ${calendar.number_of_vendors_to_schedule * 3}hrs of availability required`}
              </CoreText.Paragraph>
            </ValidationCheck>
          )}
        </div>
        <div className="tw:flex tw:flex-col tw:px-4">
          <CoreButton
            type="button"
            kind={!valid || sendingRescheduleRequest ? 'disabled' : 'primary'}
            text="Confirm availability"
            disabled={!valid || sendingRescheduleRequest}
            onClick={handleConfirm}
          />
        </div>
      </div>
    </div>
  )
}

export default UpdateAvailability
